import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Typography,
} from "@mui/material";
import default_theme from "assets/themes/themes";
import { blueTextFieldStyles } from "assets/themes/textFieldStyles";

const CCP_OPTIONS = [
  "Climate Change Adaptation",
  "Biodiversity",
  "Social Justice",
];

const TerritorialChallengeDialog = ({
  open,
  onClose,
  onSave,
  formData,
  setFormData,
  rdds,
}) => {
  if (!formData) return null;

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const selectedCCPs = formData.cross_cutting_priority
    ? formData.cross_cutting_priority.split(", ")
    : [];

  const handleCCPChange = (event) => {
    const value = event.target.value;
    handleFieldChange(
      "cross_cutting_priority",
      typeof value === "string" ? value : value.join(", ")
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 0 }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}
        >
          {formData.id ? "Edit Territorial Challenge" : "Add a New Challenge"}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
      >
        <TextField
          label="Title (Short name for the challenge)"
          value={formData.title}
          onChange={(e) => handleFieldChange("title", e.target.value)}
          fullWidth
          required
          sx={blueTextFieldStyles(default_theme)}
        />

        <TextField
          label="Description"
          placeholder="Describe the current/future difficulty in detail..."
          value={formData.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          fullWidth
          multiline
          rows={4}
          sx={blueTextFieldStyles(default_theme)}
        />

        <TextField
          select
          label="Primary Rural Development Driver (RDD) Affected"
          value={formData.rdd_id || ""}
          onChange={(e) =>
            handleFieldChange("rdd_id", e.target.value === "" ? null : e.target.value)
          }
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>

          {rdds.map((rdd) => (
            <MenuItem key={rdd.id} value={rdd.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {rdd.image_url && (
                  <Box
                    component="img"
                    src={rdd.image_url}
                    alt={rdd.name}
                    sx={{ width: 24, height: 24, objectFit: "contain" }}
                  />
                )}
                {rdd.name}
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Cross-Cutting Priorities (CCP) Intersected"
          value={selectedCCPs}
          onChange={handleCCPChange}
          fullWidth
          sx={blueTextFieldStyles(default_theme)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => selected.join(", "),
          }}
        >
          {CCP_OPTIONS.map((name) => {
            const isSelected = selectedCCPs.includes(name);

            return (
              <MenuItem 
                key={name} 
                value={name}
                // Prevents the background from swallowing the checkbox checkmark
                sx={{
                  "&.Mui-selected": { backgroundColor: "transparent" },
                  "&.Mui-selected:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" }
                }}
              >
                <Checkbox 
                  checked={isSelected} 
                  sx={{
                    color: "#667085", // Empty box color
                    "&.Mui-checked": {
                      color: "#23576B", // Checked box color - forces it to be visible
                    }
                  }}
                />
                <ListItemText primary={name} />
              </MenuItem>
            );
          })}
        </TextField>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{ color: "#667085", textTransform: "none", fontWeight: 600 }}
        >
          Cancel
        </Button>

        <Button
          onClick={onSave}
          variant="contained"
          disabled={!formData.title}
          sx={{
            backgroundColor: "#23576B",
            color: "#ffffff",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#1B4454" },
          }}
        >
          Save Challenge
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TerritorialChallengeDialog;